import * as React from "react";
import { CalendarRange, Pencil, X } from "lucide-react";
import { Link } from "react-router-dom";

import {
  ApiError,
  createAvailabilityPattern,
  deleteAvailabilityPattern,
  listAvailabilityPatterns,
  setDefaultAvailabilityPattern,
  updateAvailabilityPattern,
} from "@/api/client";
import { AvailabilityRangeInput, AvailabilityPatternView, WEEKDAYS } from "@/api/types";
import { ClockTimePicker } from "@/components/ClockTimePicker";
import { rollingLinkLinkFor } from "@/features/booking/links";

import {
  AVAILABILITY_PATTERNS_SECTION_ID,
  availabilityPatternAnchorId,
  availabilityPatternLinkFor,
} from "./patternLinks";

/**
 * Manage the caller's own availability patterns — the templates they invite people with.
 *
 * <p>This replaced a checkbox list of six fixed categories the user could
 * "accept". A type is now theirs: a name they chose plus the hours it offers.
 * Each card saves itself against the per-type CRUD endpoints rather than riding
 * along on a profile PATCH.</p>
 *
 * <p>A type may hold several availability ranges, and two of them may share a
 * weekday as long as they don't overlap — that is exactly how you say "mornings
 * and afternoons, but not lunch".</p>
 */
export function AvailabilityPatternsEditor() {
  const [types, setTypes] = React.useState<AvailabilityPatternView[] | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const [creating, setCreating] = React.useState(false);

  const reload = React.useCallback(async () => {
    try {
      setTypes(await listAvailabilityPatterns());
    } catch (e) {
      setError(describeError(e));
    }
  }, []);

  React.useEffect(() => {
    void reload();
  }, [reload]);

  // Scrolling to a #hash is ProfilePage's job: it polls until the target exists, which is what an
  // anchor on a card sitting behind a fetch needs. This component only has to render the ids.
  //
  // Display order only — `types` itself stays as the server sent it, because the counts below
  // ("is there more than one pattern I could delete") read it.
  const ordered = React.useMemo(
    () => (types === null ? null : orderAvailabilityPatterns(types)),
    [types],
  );

  async function run(action: () => Promise<unknown>) {
    setError(null);
    try {
      await action();
      await reload();
    } catch (e) {
      setError(describeError(e));
    }
  }

  if (types === null || ordered === null) {
    return <p className="text-xs text-muted-foreground">Loading…</p>;
  }

  return (
    // id + scroll-mt: deep-link target for "Edit availability" on the booking-page
    // builder (/profile#availability-patterns). scroll-mt clears the sticky AppNav so the
    // heading isn't parked underneath it.
    <fieldset
      id={AVAILABILITY_PATTERNS_SECTION_ID}
      className="scroll-mt-24 space-y-2 border-t-2 border-dashed border-border pt-4"
    >
      <legend className="font-comic-headline text-sm uppercase tracking-wide text-foreground">
        Availability patterns
      </legend>

      {error && (
        <p role="alert" className="rounded border-2 border-destructive bg-destructive/10 px-3 py-2 text-xs text-destructive-strong">
          {error}
        </p>
      )}

      <div className="space-y-2">
        {ordered.map((type) =>
          type.owned_by_link ? (
            <LinkOwnedPatternCard key={type.id} type={type} />
          ) : (
          <AvailabilityPatternCard
            key={type.id}
            type={type}
            canDelete={types.filter((t) => !t.owned_by_link).length > 1}
            others={types.filter((t) => t.id !== type.id && !t.owned_by_link)}
            onSave={(req) => run(() => updateAvailabilityPattern(type.id, req))}
            onMakeDefault={() => run(() => setDefaultAvailabilityPattern(type.id))}
            onDelete={(promoteId) => run(() => deleteAvailabilityPattern(type.id, promoteId))}
          />
          ),
        )}
      </div>

      {creating ? (
        <NewAvailabilityPatternCard
          onCancel={() => setCreating(false)}
          onCreate={async (req) => {
            await run(() => createAvailabilityPattern(req));
            setCreating(false);
          }}
        />
      ) : (
        <button
          type="button"
          onClick={() => setCreating(true)}
          className="rounded border border-border bg-background px-3 py-1.5 text-xs font-comic-headline uppercase tracking-wide hover:bg-muted"
        >
          + Add availability pattern
        </button>
      )}
    </fieldset>
  );
}

// --------------------------------------------------------------------- cards

interface CardProps {
  type: AvailabilityPatternView;
  canDelete: boolean;
  others: AvailabilityPatternView[];
  onSave: (req: { name: string; ranges: AvailabilityRangeInput[] }) => void;
  onMakeDefault: () => void;
  onDelete: (promoteId?: string) => void;
}

/**
 * A pattern a rolling link owns: shown, named after its link, and not editable here.
 *
 * <p>The link rewrites this pattern on every save, so a field to type in would be a field whose
 * contents vanish. Showing it read-only answers the question the host actually has — where do this
 * link's hours come from — and points at the one place that can change them (D-88).</p>
 */
function LinkOwnedPatternCard({ type }: { type: AvailabilityPatternView }) {
  const link = type.owned_by_link!;
  return (
    <div
      id={availabilityPatternAnchorId(type.id)}
      className="scroll-mt-24 rounded-md border-2 border-brand-teal bg-background p-3"
    >
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-comic-headline text-sm">{type.name}</span>
        <span className="rounded-full border border-brand-teal px-2 py-0.5 text-[11px] uppercase tracking-wide text-brand-teal">
          Owned by a link
        </span>
        {/*
          The walk out to the link that owns these hours: its builder, not the list it sits in.
          `from.to` names this exact card rather than leaving BackBar to pop one history entry,
          because popping lands on /profile with no hash — the browser restores roughly the right
          scroll and nothing tells the host which of their patterns they were looking at. Naming
          it re-runs the scroll-and-flash, so the return trip lands where the trip started.
        */}
        <Link
          to={rollingLinkLinkFor(link.id)}
          state={{
            from: {
              label: "your availability patterns",
              to: availabilityPatternLinkFor(type.id),
            },
          }}
          className="ml-auto text-xs underline hover:text-foreground"
          title={`Open “${link.title}” — the booking link that sets these hours`}
        >
          {link.title} →
        </Link>
      </div>

      <div className="mt-2 sm:basis-2/5">
        <WeekAvailabilityMini ranges={type.ranges} />
      </div>
    </div>
  );
}

function AvailabilityPatternCard({ type, canDelete, others, onSave, onMakeDefault, onDelete }: CardProps) {
  const [name, setName] = React.useState(type.name);
  const [ranges, setRanges] = React.useState<AvailabilityRangeInput[]>(type.ranges);
  const [localError, setLocalError] = React.useState<string | null>(null);

  // Re-seed when the server hands us a fresh copy (after a save elsewhere).
  React.useEffect(() => {
    setName(type.name);
    setRanges(type.ranges);
  }, [type]);

  function save() {
    const problem = validateAvailabilityPattern(name, ranges);
    setLocalError(problem);
    if (!problem) onSave({ name, ranges });
  }

  return (
    <div
      id={availabilityPatternAnchorId(type.id)}
      className="scroll-mt-24 rounded-md border border-border bg-background p-3"
    >
      {/* Header spans the full card so the delete sits at the top-right
          corner, clear of the week preview below. */}
      <div className="flex items-center gap-2">
        <EditableName value={name} onChange={setName} ariaLabel="Availability pattern name" />
        {type.is_default ? (
          <span className="shrink-0 rounded-full border border-border bg-brand-blue px-2 py-0.5 text-[11px] font-comic-headline uppercase text-brand-blue-foreground">
            ★ Default
          </span>
        ) : (
          <button
            type="button"
            onClick={onMakeDefault}
            className="shrink-0 rounded-full border border-border px-2 py-0.5 text-[11px] font-comic-headline uppercase hover:bg-brand-blue/10"
          >
            Make default
          </button>
        )}
        {canDelete && (
          <DeleteButton isDefault={type.is_default} others={others} onDelete={onDelete} />
        )}
      </div>

      <div className="mt-2 flex flex-col gap-3 sm:flex-row sm:items-start">
        {/* 60% — the availability editor; 40% — the week preview. */}
        <div className="min-w-0 sm:basis-3/5">
          <RangeList ranges={ranges} onChange={setRanges} />
        </div>
        <div className="sm:basis-2/5 sm:shrink-0">
          <WeekAvailabilityMini ranges={ranges} />
        </div>
      </div>

      {localError && (
        <p role="alert" className="mt-2 text-xs text-destructive-strong">
          {localError}
        </p>
      )}

      <div className="mt-3 flex flex-wrap items-center gap-2">
        <AddRangeButton onClick={() => setRanges([...ranges, blankRange()])} />
        <SaveButton onClick={save}>Save</SaveButton>
      </div>

    </div>
  );
}

function NewAvailabilityPatternCard({
  onCreate,
  onCancel,
}: {
  onCreate: (req: { name: string; ranges: AvailabilityRangeInput[] }) => void;
  onCancel: () => void;
}) {
  const [name, setName] = React.useState("");
  const [ranges, setRanges] = React.useState<AvailabilityRangeInput[]>([blankRange()]);
  const [localError, setLocalError] = React.useState<string | null>(null);

  return (
    <div className="rounded-md border-2 border-dashed border-border bg-background p-3">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start">
        <div className="min-w-0 space-y-2 sm:basis-3/5">
          <div className="flex">
            <EditableName
              value={name}
              onChange={setName}
              ariaLabel="New availability pattern name"
              placeholder="Client onboarding"
            />
          </div>
          <RangeList ranges={ranges} onChange={setRanges} />
        </div>
        <div className="sm:basis-2/5 sm:shrink-0">
          <WeekAvailabilityMini ranges={ranges} />
        </div>
      </div>
      {localError && (
        <p role="alert" className="mt-2 text-xs text-destructive-strong">
          {localError}
        </p>
      )}
      <div className="mt-3 flex flex-wrap items-center gap-2">
        <AddRangeButton onClick={() => setRanges([...ranges, blankRange()])} />
        <SaveButton
          onClick={() => {
            const problem = validateAvailabilityPattern(name, ranges);
            setLocalError(problem);
            if (!problem) onCreate({ name, ranges });
          }}
        >
          Create
        </SaveButton>
        <button
          type="button"
          onClick={onCancel}
          className="rounded-full border border-border bg-background px-4 py-1.5 text-xs font-comic-headline uppercase tracking-wide transition hover:bg-muted"
        >
          Cancel
        </button>
      </div>
    </div>
  );
}

/**
 * Deleting the default needs a successor: the server refuses to leave a user
 * without one, so ask here rather than surface a 409.
 */
function DeleteButton({
  isDefault,
  others,
  onDelete,
}: {
  isDefault: boolean;
  others: AvailabilityPatternView[];
  onDelete: (promoteId?: string) => void;
}) {
  const [promoteId, setPromoteId] = React.useState(others[0]?.id ?? "");
  const [open, setOpen] = React.useState(false);

  // A non-default type deletes on the spot. The default can't leave the user
  // without one, so its ✕ opens a little popover to pick the successor first.
  if (!isDefault) {
    return (
      <RoundClose
        label="Delete this availability pattern"
        onClick={() => onDelete()}
        className="ml-auto shrink-0"
      />
    );
  }
  return (
    <div className="relative ml-auto shrink-0">
      <RoundClose
        label="Delete this availability pattern"
        onClick={() => setOpen((o) => !o)}
      />
      {open && (
        <>
          <button
            type="button"
            aria-hidden
            tabIndex={-1}
            onClick={() => setOpen(false)}
            className="fixed inset-0 z-20 cursor-default"
          />
          <div className="absolute right-0 top-9 z-30 w-60 rounded-xl border border-border bg-background p-3 text-left shadow-lg">
            <p className="mb-2 text-[11px] leading-snug text-muted-foreground">
              This is your default type. Choose a new default before deleting it.
            </p>
            <select
              value={promoteId}
              onChange={(e) => setPromoteId(e.target.value)}
              aria-label="Promote this type to default"
              className="mb-3 w-full rounded-md border border-border bg-background px-2 py-1.5 text-xs"
            >
              {others.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name}
                </option>
              ))}
            </select>
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="rounded-full border border-border bg-background px-3 py-1 text-xs font-comic-headline uppercase tracking-wide hover:bg-muted"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => {
                  onDelete(promoteId);
                  setOpen(false);
                }}
                className="rounded-full border border-destructive/30 bg-destructive/10 px-3 py-1 text-xs font-comic-headline uppercase tracking-wide text-destructive-strong hover:bg-destructive/20"
              >
                Delete
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

/** The card's primary action — a rounded pill with a soft comic shadow. */
function SaveButton({
  onClick,
  children,
}: {
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="comic-shadow-sm rounded-full border border-border bg-brand-blue text-brand-blue-foreground px-4 py-1.5 text-xs font-comic-headline uppercase tracking-wide transition hover:translate-x-[1px] hover:translate-y-[1px] hover:brightness-110 hover:shadow-none"
    >
      {children}
    </button>
  );
}

/**
 * An inline-editable name: reads as a plain heading, not a form field, but a
 * persistent pencil hint and a hover highlight signal it's editable, and a
 * clear ring + solid fill mark edit mode on focus. The click-to-edit pattern
 * used by Notion / Linear / Trello titles.
 */
function EditableName({
  value,
  onChange,
  ariaLabel,
  placeholder,
}: {
  value: string;
  onChange: (next: string) => void;
  ariaLabel: string;
  placeholder?: string;
}) {
  return (
    <div className="group relative flex min-w-0 flex-1 items-center">
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        maxLength={40}
        aria-label={ariaLabel}
        placeholder={placeholder}
        className="peer min-w-0 flex-1 rounded-md border border-transparent bg-transparent py-1 pl-1.5 pr-7 font-comic-headline text-sm uppercase tracking-wide transition placeholder:normal-case placeholder:text-muted-foreground hover:bg-muted/50 focus:border-brand-blue focus:bg-background focus:outline-none focus:ring-1 focus:ring-brand-blue"
      />
      <Pencil
        aria-hidden
        className="pointer-events-none absolute right-2 h-3.5 w-3.5 text-brand-blue/60 transition peer-hover:text-brand-blue peer-focus:text-brand-blue"
      />
    </div>
  );
}

/**
 * A small circular close/remove control — an ✕ in a shadowed disc that reddens
 * on hover. Used to drop an availability pattern or an availability range.
 */
function RoundClose({
  label,
  onClick,
  className = "",
}: {
  label: string;
  onClick: () => void;
  className?: string;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      onClick={onClick}
      className={
        "grid h-7 w-7 shrink-0 place-items-center rounded-full border border-destructive/20 bg-destructive/10 text-destructive-strong shadow-sm transition hover:-translate-y-px hover:border-destructive/30 hover:bg-destructive/20 hover:text-destructive-strong hover:shadow " +
        className
      }
    >
      <X className="h-3.5 w-3.5" strokeWidth={2.5} />
    </button>
  );
}

// -------------------------------------------------------------------- ranges

/**
 * One row per availability range: weekday pills plus a start/end time. Compact
 * on purpose — a per-weekday layout would need seven rows and still could not
 * express two windows on the same day, which is the whole point.
 */
function RangeList({
  ranges,
  onChange,
}: {
  ranges: AvailabilityRangeInput[];
  onChange: (next: AvailabilityRangeInput[]) => void;
}) {
  function patch(index: number, patchValue: Partial<AvailabilityRangeInput>) {
    onChange(ranges.map((r, i) => (i === index ? { ...r, ...patchValue } : r)));
  }

  function toggleDay(index: number, day: string) {
    const current = ranges[index].days;
    const next = current.includes(day)
      ? current.filter((d) => d !== day)
      : WEEKDAYS.filter((d) => d === day || current.includes(d));
    patch(index, { days: next });
  }

  return (
    <div className="space-y-1.5">
      {ranges.map((range, index) => (
        // Borderless canvas: faint outer, medium day/time subpanels; the delete sits in the
        // top-right corner, offset so it's hard to hit by accident.
        <div
          key={index}
          className="relative rounded-lg bg-muted/20 p-2 transition hover:bg-muted/40"
        >
          {ranges.length > 1 && (
            <RoundClose
              label="Remove this range"
              onClick={() => onChange(ranges.filter((_, i) => i !== index))}
              className="absolute right-1.5 top-1.5"
            />
          )}

          <div className="mb-1.5 pr-7 text-[11px] font-semibold text-foreground/70">
            {summariseRange(range)}
          </div>

          <div className="space-y-1.5">
            <div className="rounded-md bg-muted/50 p-1.5 transition hover:bg-muted/70">
              <div className="flex flex-wrap gap-1">
                {WEEKDAYS.map((day) => {
                  const on = range.days.includes(day);
                  return (
                    <button
                      key={day}
                      type="button"
                      aria-pressed={on}
                      onClick={() => toggleDay(index, day)}
                      className={
                        "rounded-md border px-1.5 py-0.5 text-xs font-comic-headline uppercase tracking-wide transition " +
                        (on
                          ? "border-brand-blue bg-brand-blue text-brand-blue-foreground"
                          : "border-border bg-background hover:border-brand-blue/30 hover:bg-brand-blue/10")
                      }
                    >
                      {day.slice(0, 3)}
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="rounded-md bg-muted/50 p-1.5 transition hover:bg-muted/70">
              <div className="flex flex-wrap items-center gap-2 text-sm">
                <span className="text-muted-foreground">Between</span>
                <ClockTimePicker
                  ariaLabel="Earliest time"
                  value={range.earliest}
                  onChange={(v) => patch(index, { earliest: v })}
                />
                <span className="text-muted-foreground">and</span>
                <ClockTimePicker
                  ariaLabel="Latest time"
                  value={range.latest}
                  onChange={(v) => patch(index, { latest: v })}
                />
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

/** The "+ Add availability range" pill, rendered inline beside Save/Create by the parents. */
function AddRangeButton({ onClick }: { onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="rounded-full border-2 border-dashed border-border px-4 py-1.5 text-xs font-comic-headline uppercase tracking-wide text-foreground/70 transition hover:border-brand-blue/30 hover:bg-brand-blue/10 hover:text-foreground"
    >
      + Add availability range
    </button>
  );
}

// ---------------------------------------------------------------- validation

// A compact weekly picture of a type's availability, shown beside the editor —
// same idea as the negotiation panel's timeline, just tidier. Teal blocks land on
// each selected weekday, positioned by time of day.
const MINI_START = 6 * 60; // 06:00
const MINI_END = 22 * 60; // 22:00

function toMinutes(hhmm: string): number {
  const [h, m] = hhmm.split(":").map(Number);
  return (h || 0) * 60 + (m || 0);
}

const WEEKDAYS_ONLY = WEEKDAYS.slice(0, 5);
const WEEKEND_ONLY = WEEKDAYS.slice(5);

/** A plain-language caption for a range: "Weekdays · 08:00–12:00". */
function summariseRange(range: AvailabilityRangeInput): string {
  const days = range.days;
  const cap = (d: string) => d.charAt(0).toUpperCase() + d.slice(1, 3);
  const sameSet = (a: string[], b: string[]) =>
    a.length === b.length && b.every((d) => a.includes(d));

  let label: string;
  if (days.length === 0) label = "No days";
  else if (days.length === 7) label = "Every day";
  else if (sameSet(days, WEEKDAYS_ONLY)) label = "Weekdays";
  else if (sameSet(days, WEEKEND_ONLY)) label = "Weekends";
  else label = WEEKDAYS.filter((d) => days.includes(d)).map(cap).join(", ");

  return `${label} · ${range.earliest}–${range.latest}`;
}

// Hour guides drawn faintly behind the day columns — enough to read "morning"
// vs "afternoon" without cluttering the little chart.
const MINI_GUIDES = [9 * 60, 12 * 60, 15 * 60, 18 * 60];

function WeekAvailabilityMini({ ranges }: { ranges: AvailabilityRangeInput[] }) {
  const span = MINI_END - MINI_START;
  const pctOf = (min: number) => ((min - MINI_START) / span) * 100;
  return (
    <div className="rounded-xl bg-gradient-to-b from-brand-blue/10 to-brand-blue/5 p-2.5 shadow-sm transition hover:shadow-md hover:ring-1 hover:ring-brand-blue/30">
      <span className="mb-2 flex items-center gap-1 text-[10px] font-bold uppercase tracking-wide text-foreground">
        <CalendarRange className="h-3 w-3" /> Week preview
      </span>
      <div className="grid grid-cols-7 gap-1">
        {WEEKDAYS.map((day, di) => (
          <div
            key={`h-${day}`}
            className={
              "text-center text-[9px] font-bold uppercase " +
              (di >= 5 ? "text-foreground/50" : "text-foreground")
            }
          >
            {day[0]}
          </div>
        ))}
        {WEEKDAYS.map((day, di) => {
          const weekend = di >= 5;
          return (
            <div
              key={day}
              className={
                "relative h-28 overflow-hidden rounded-md " +
                (weekend ? "bg-foreground/[0.03]" : "bg-background/70 shadow-inner")
              }
            >
              {/* faint hour guides */}
              {MINI_GUIDES.map((g) => (
                <div
                  key={g}
                  className="absolute inset-x-0 border-t border-dashed border-foreground/[0.07]"
                  style={{ top: `${pctOf(g)}%` }}
                />
              ))}
              {ranges
                .filter((r) => r.days.includes(day))
                .map((r, i) => {
                  const a = Math.max(toMinutes(r.earliest), MINI_START);
                  const b = Math.min(toMinutes(r.latest), MINI_END);
                  if (b <= a) return null;
                  return (
                    <div
                      key={i}
                      title={`${r.earliest}–${r.latest}`}
                      className="absolute inset-x-[2px] rounded bg-gradient-to-b from-brand-blue/80 to-brand-blue shadow-sm ring-1 ring-inset ring-brand-blue/25"
                      style={{
                        top: `${pctOf(a)}%`,
                        height: `${((b - a) / span) * 100}%`,
                      }}
                    />
                  );
                })}
            </div>
          );
        })}
      </div>
    </div>
  );
}

/**
 * Link-owned patterns first, everything else in the order the server sent.
 *
 * <p>They outrank the ones the host typed: a link's hours are what an invitee is actually
 * offered, they are rewritten under the host whenever that link is saved, and they are the ones
 * a host arrives here confused about — so they should not be buried under a list of patterns
 * nothing is currently pointing at. Within each group the server's order (oldest first) stands,
 * which Array.prototype.sort guarantees now that it is specified as stable.</p>
 */
export function orderAvailabilityPatterns(
  types: AvailabilityPatternView[],
): AvailabilityPatternView[] {
  return [...types].sort(
    (a, b) => Number(Boolean(b.owned_by_link)) - Number(Boolean(a.owned_by_link)),
  );
}

function blankRange(): AvailabilityRangeInput {
  return { days: WEEKDAYS.slice(0, 5), earliest: "09:00", latest: "17:00" };
}

/**
 * Mirror the server's rules so the user hears about a problem before paying for
 * a round trip. Overlaps are rejected rather than merged: merging would rewrite
 * what they typed, and the calendar scans each range separately.
 */
export function validateAvailabilityPattern(
  name: string,
  ranges: AvailabilityRangeInput[],
): string | null {
  if (!name.trim()) return "Give this availability pattern a name.";
  if (ranges.length === 0) return "Add at least one availability range.";

  for (const range of ranges) {
    if (range.days.length === 0) return "Each range needs at least one weekday.";
    if (!range.earliest || !range.latest) return "Each range needs a start and an end time.";
    if (range.earliest >= range.latest) {
      return `"${range.earliest}" must be earlier than "${range.latest}".`;
    }
  }
  for (let i = 0; i < ranges.length; i++) {
    for (let j = i + 1; j < ranges.length; j++) {
      if (overlaps(ranges[i], ranges[j])) {
        return "Two ranges overlap on the same day. Leave a gap between them.";
      }
    }
  }
  return null;
}

/** Share a weekday AND intersect in time. Touching (12:00 / 12:00) is fine. */
function overlaps(a: AvailabilityRangeInput, b: AvailabilityRangeInput): boolean {
  const sharesDay = a.days.some((d) => b.days.includes(d));
  return sharesDay && a.earliest < b.latest && b.earliest < a.latest;
}

function describeError(e: unknown): string {
  if (e instanceof ApiError) {
    // 402 = entitlement quota hit (the @RequiresQuota gate). Show a plan message, not a raw code.
    if (e.status === 402) {
      let limit: number | undefined;
      try {
        limit = JSON.parse(e.body || "{}").limit;
      } catch {
        /* no body */
      }
      return limit != null
        ? `You've reached your plan's limit of ${limit} availability pattern${limit === 1 ? "" : "s"}. Upgrade to add more.`
        : "You've reached your plan's availability-pattern limit. Upgrade to add more.";
    }
    try {
      const parsed = JSON.parse(e.body || "{}");
      if (typeof parsed.message === "string") return parsed.message;
      if (typeof parsed.error === "string") return parsed.error;
    } catch {
      // fall through to the generic message
    }
    return `${e.status}: ${e.body || e.message}`;
  }
  return String(e);
}
